import {
  ColumnDef,
  PaginationState,
  Row,
  RowSelectionState,
  SortingState,
  TableState,
  Updater,
} from '@tanstack/react-table';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui/Stack';

import { GroupHeaderRow } from './components/GroupHeaderRow';
import { Table as TableComponents } from './components/Table';
import { TablePagination } from './components/TablePagination';
import * as styles from './GroupedTable.css';
import { useTableSetup } from './hooks/useTableSetup';
import {
  RowWrapper,
  TablePaginationConfig,
  TableRowHeightVariant,
} from './types';

interface GroupedTableV2Props<T = any> {
  actionColId?: string;
  data: T[];
  columns: ColumnDef<T>[];
  variant?: TableRowHeightVariant;
  bordered?: boolean;
  alwaysShowCheckbox?: boolean;
  showSelectAllCheckbox?: boolean;
  getGroupId?: (data: T) => string | null | undefined; // Returns null/undefined for ungrouped rows
  getItemId?: (data: T) => string;
  getRowId?: (data: T, index: number) => string;
  isSelectionDisabled?: (data: T) => string | undefined;
  enableRowSelection?: boolean | ((row: Row<T>) => boolean);
  enableMultiRowSelection?: boolean | ((row: Row<T>) => boolean);
  enableSubRowSelection?: boolean | ((row: Row<T>) => boolean);
  onRowSelectionChange?: (updater: Updater<RowSelectionState>) => void;
  onExpandedChange?: (updater: any) => void; // For hierarchical data
  onPaginationChange?: (updater: Updater<PaginationState>) => void;
  onSortingChange?: (updater: Updater<SortingState>) => void;
  onFilteredRowsChange?: (rows: Row<T | null>[] | undefined) => void;
  activeRowIds?: Set<string>;
  onRowSelect?: (
    tableRows: Row<T | null>[] | undefined,
    row: Row<T | null>,
    index: number
  ) => void;
  pagination?: TablePaginationConfig;
  className?: string;
  summaryData?: T | null;
  style?: React.CSSProperties;
  state?: Partial<TableState>;
  sortingFns?: Record<string, any>;
  meta?: Record<string, any>;
  footerCta?: React.ReactNode;
  rowWrapper?: RowWrapper<T>;
  renderGroupHeader?: (groupId: string, groupData: T[]) => React.ReactNode;
  getSubRows?: (row: T) => T[]; // For hierarchical data
  isGroupRow?: (data: T) => boolean; // Identifies if a row is a group parent
  processRows?: (rows: Row<T>[], isGroupRow?: (data: T) => boolean) => Row<T>[]; // Optional custom row processing
}

export const GroupedSimpleTable = <T extends Record<string, any>>({
  actionColId,
  data,
  columns,
  variant = 'normal',
  alwaysShowCheckbox,
  getGroupId,
  getRowId,
  getItemId,
  isSelectionDisabled,
  onRowSelectionChange,
  onExpandedChange,
  onPaginationChange,
  onSortingChange,
  onFilteredRowsChange,
  enableRowSelection,
  enableMultiRowSelection,
  enableSubRowSelection,
  activeRowIds,
  onRowSelect,
  pagination,
  className,
  summaryData,
  state,
  sortingFns,
  meta,
  footerCta,
  rowWrapper,
  renderGroupHeader,
  getSubRows,
  isGroupRow,
  processRows,
}: GroupedTableV2Props<T>) => {
  // Parent (ListingTableV2) manages expanded state - just use it
  const {
    table,
    tableColumns,
    renderHeaderGroups,
    renderRowContent,
    showCheckbox,
  } = useTableSetup({
    actionColId,
    data,
    columns,
    alwaysShowCheckbox,
    getGroupId,
    getItemId,
    isSelectionDisabled,
    onRowSelectionChange,
    onExpandedChange,
    onPaginationChange,
    onSortingChange,
    onFilteredRowsChange,
    enableRowSelection,
    enableMultiRowSelection,
    enableSubRowSelection,
    pagination,
    summaryData,
    state, // Use state from parent (includes expanded)
    sortingFns,
    meta,
    getSubRows, // Pass to TanStack for hierarchy handling
    isGroupRow, // Pass to determine which rows can expand
    getRowId,
    getRowCanExpand: isGroupRow
      ? (row) => Boolean(isGroupRow(row.original))
      : undefined,
  });

  // Measure container width with ResizeObserver and set as CSS variable
  const containerRef = useRef<HTMLDivElement>(null);
  const TableContainerWidth = '--table-container-width';

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const resizeObserver = new ResizeObserver((entries) => {
      requestAnimationFrame(() => {
        for (const entry of entries) {
          const width = Math.round(entry.contentRect.width);
          element.style.setProperty(TableContainerWidth, `${width}px`);
        }
      });
    });

    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, [TableContainerWidth]);

  const rawTableRows = table.getRowModel().rows;
  const tableRows = useMemo(
    () => (processRows ? processRows(rawTableRows, isGroupRow) : rawTableRows),
    [rawTableRows, processRows, isGroupRow]
  );

  const handleClick = useCallback(
    (row: Row<T>, e: React.MouseEvent, index: number) => {
      e.stopPropagation();
      onRowSelect?.(tableRows as Row<T | null>[], row as Row<T | null>, index);
    },
    [onRowSelect, tableRows]
  );
  // Create flat array of rows in visual order for shift-select
  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <TableComponents.Root
        table={table}
        variant={variant}
        className={className}
      >
        <TableComponents.Header>
          <TableComponents.Row disableHover className={styles.headerRow}>
            {renderHeaderGroups()}
          </TableComponents.Row>
        </TableComponents.Header>
        <tbody className={styles.groupTbody}>
          <>
            {tableRows.length > 0 &&
              tableRows.map((row, index) => {
                const isParentRow =
                  row.depth === 0 && isGroupRow?.(row.original);
                if (isParentRow) {
                  const groupId = getItemId?.(row.original) ?? row.id;
                  const childData = row.subRows?.map((r) => r.original) || [];
                  const isExpanded = row.getIsExpanded();

                  // Skip rendering parent rows here; they will be rendered with their children
                  return (
                    <TableComponents.Row
                      key={row.id}
                      selectable={Boolean(onRowSelect)}
                      selected={row.getIsSelected()}
                      onClick={(e: React.MouseEvent) =>
                        handleClick(row, e, index)
                      }
                    >
                      <GroupHeaderRow
                        groupId={groupId}
                        childData={childData}
                        isExpanded={isExpanded}
                        toggleExpanded={() => row.toggleExpanded()}
                        renderGroupHeader={renderGroupHeader}
                        showCheckbox={showCheckbox}
                        parentRow={row}
                        table={table}
                        getGroupId={getGroupId}
                        selected={
                          row.getIsSelected() ||
                          Boolean(activeRowIds?.has(row.id))
                        }
                        getItemId={getItemId}
                        isSelectionDisabled={isSelectionDisabled}
                        tableContainerWidth={TableContainerWidth}
                      />
                    </TableComponents.Row>
                    // </React.Fragment>
                  );
                }
                const cellContent = renderRowContent(row);
                const wrappedContent = rowWrapper
                  ? rowWrapper(row.original, cellContent, index)
                  : cellContent;

                return (
                  <TableComponents.Row
                    key={row.id}
                    onClick={(e: React.MouseEvent) =>
                      handleClick(row, e, index)
                    }
                    selected={
                      row.getIsSelected() || Boolean(activeRowIds?.has(row.id))
                    }
                    selectable={Boolean(handleClick)}
                  >
                    {wrappedContent}
                  </TableComponents.Row>
                );
              })}
          </>
        </tbody>

        <TableComponents.Footer />
      </TableComponents.Root>

      <Stack
        justifyContent="spaceBetween"
        style={{ marginTop: vars.spacing.m }}
      >
        {pagination && (
          <TablePagination
            table={table}
            pageSize={pagination.pageSize}
            variant={pagination.variant}
          />
        )}
        {footerCta}
      </Stack>
    </div>
  );
};
